import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

// PrimeNG Imports
import { ToastrService } from 'ngx-toastr';

// Services
import { FormBuilderService } from 'src/app/private/services/form-builder.service';
import { DynamicForm, FormFieldConfig } from 'src/app/private/modules/configuration/form-builder/models/form-builder.model';
import { SharedModule } from 'src/app/private/shared/shared.module';

@Component({
  selector: 'app-public-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule
  ],
  templateUrl: './public-form.component.html',
  styleUrls: ['./public-form.component.scss']
})
export class PublicFormComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  formData: DynamicForm | null = null;
  dynamicForm: FormGroup;
  loading = true;
  submitting = false;
  submitted = false;
  error: string | null = null;
  publicToken: string = '';
  formRows: any[] = []; // Organized rows with fields

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private formBuilderService: FormBuilderService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) {
    this.dynamicForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.publicToken = this.route.snapshot.paramMap.get('token') || '';

    if (this.publicToken) {
      this.loadPublicForm();
    } else {
      this.error = 'Invalid form link';
      this.loading = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadPublicForm(): void {
    this.loading = true;
    this.formBuilderService.getPublicForm(this.publicToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.formData = response.data || response;
          // Map form_field_config to fields if it exists
          if (this.formData) {
            if ((this.formData as any).form_field_config && !this.formData.fields) {
              this.formData.fields = (this.formData as any).form_field_config;
            }
            this.organizeFieldsByRows();
            this.buildDynamicForm();
          }
          this.loading = false;
        },
        error: (error) => {
          this.error = error.error?.message || 'Form not found or is no longer available';
          this.loading = false;
        }
      });
  }

  buildDynamicForm(): void {
    if (!this.formData || !this.formData.fields) return;

    const group: any = {};

    this.formData.fields
      .sort((a, b) => a.order - b.order)
      .forEach((fieldConfig: FormFieldConfig) => {
        // Skip fields without field_id
        if (fieldConfig.field_id === null || fieldConfig.field_id === undefined) return;

        // Convert field_id to string to ensure FormGroup key consistency
        const fieldId = String(fieldConfig.field_id);

        const validators = [];

        if (fieldConfig.is_required) {
          validators.push(Validators.required);
        }

        // Add specific validators based on UI type
        if (fieldConfig.ui_type === 'email') {
          validators.push(Validators.email);
        }

        group[fieldId] = [
          fieldConfig.default_value || this.getDefaultValue(fieldConfig),
          validators
        ];
      });

    this.dynamicForm = this.fb.group(group);
  }

  getDefaultValue(fieldConfig: FormFieldConfig): any {
    switch (fieldConfig.field.data_type) {
      case 'boolean':
        return false;
      case 'number':
        return null;
      case 'date':
        return null;
      default:
        return '';
    }
  }

  submitForm(): void {
    if (this.dynamicForm.invalid) {
      this.dynamicForm.markAllAsTouched();
      this.toastr.error('Please fill all required fields'
      );
      return;
    }

    this.submitting = true;

    const staticFields: any = {};
    const dynamicFields: any = {};

    this.formData?.fields.forEach(field => {
      const fieldId = String(field.field_id);
      const value = this.dynamicForm.get(fieldId)?.value;

      if (field.field_type === 'dynamic') {
        dynamicFields[field.field.field_name] = value;
      } else {
        staticFields[field.field.field_name] = value;
      }
    });

    const payload = {
      static_field: staticFields,
      dynamic_field: dynamicFields,
      public_token: this.publicToken
    };

    this.formBuilderService
      .submitPublicForm(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.submitted = true;
          this.submitting = false;
          this.toastr.success('Form submitted successfully'
          );
        },
        error: (err) => {
          this.submitting = false;
          this.toastr.error(err.error?.message || 'Submission failed'
          );
        }
      });
  }


  resetForm(): void {
    this.dynamicForm.reset();
    Object.keys(this.dynamicForm.controls).forEach(key => {
      this.dynamicForm.controls[key].markAsUntouched();
    });
  }

  getFieldLabel(fieldConfig: FormFieldConfig): string {
    return fieldConfig?.field?.label || 'Field';
  }

  // Convert field_id to string to ensure consistent form control resolution
  getFieldIdAsString(fieldId: any): string {
    if (fieldId === null || fieldId === undefined) {
      return '';
    }
    return String(fieldId);
  }

  isFieldInvalid(fieldId: any): boolean {
    const fieldIdStr = this.getFieldIdAsString(fieldId);
    if (!fieldIdStr) {
      return false;
    }
    const control = this.dynamicForm.get(fieldIdStr);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  getFieldError(fieldId: any): string {
    const fieldIdStr = this.getFieldIdAsString(fieldId);
    if (!fieldIdStr) {
      return '';
    }
    const control = this.dynamicForm.get(fieldIdStr);
    if (control?.errors) {
      if (control.errors['required']) {
        return 'This field is required';
      }
      if (control.errors['email']) {
        return 'Please enter a valid email address';
      }
    }
    return '';
  }

  getFieldOptions(fieldConfig: FormFieldConfig): any[] {
    // Check for options in fieldConfig first, then in field object
    const options = fieldConfig.options || fieldConfig.field.options;
    if (options && options.length > 0) {
      return options.map(opt => ({
        label: opt,
        value: opt
      }));
    }
    return [];
  }

  onNativeRadioChange(fieldId: any, value: any): void {
  const control = this.dynamicForm.get(String(fieldId));
  if (control) {
    control.setValue(value);
    control.markAsTouched();
  }
}


  organizeFieldsByRows(): void {
    if (!this.formData || !this.formData.fields) return;

    // Group fields by row number
    const rowMap = new Map<number, any>();

    this.formData.fields.forEach((fieldConfig: any) => {
      const rowNum = fieldConfig.row || 0;

      if (!rowMap.has(rowNum)) {
        rowMap.set(rowNum, {
          rowNumber: rowNum,
          columns: fieldConfig.row_cols || 1,
          fields: []
        });
      }

      const row = rowMap.get(rowNum);
      if (row) {
        row.fields.push(fieldConfig);
      }
    });

    // Convert map to array and sort by row number
    this.formRows = Array.from(rowMap.values()).sort((a, b) => a.rowNumber - b.rowNumber);

    // Sort fields within each row by column number
    this.formRows.forEach(row => {
      row.fields.sort((a: any, b: any) => (a.col || 0) - (b.col || 0));
    });
  }

  // Handle radio button selection via click
  onRadioSelect(fieldId: any, value: any): void {
    const fieldIdStr = this.getFieldIdAsString(fieldId);
    const control = this.dynamicForm.get(fieldIdStr);
    if (control) {
      control.setValue(value);
      control.markAsTouched();
      this.cdr.markForCheck();
    }
  }

  // Handle radio button change event from PrimeNG
  onRadioChange(event: any, fieldId: any): void {
    console.log("Radio change event:", event, "Field ID:", fieldId);
    const fieldIdStr = this.getFieldIdAsString(fieldId);
    const control = this.dynamicForm.get(fieldIdStr);
    if (control && event.value !== undefined) {
      control.setValue(event.value);
      control.markAsTouched();
      this.cdr.markForCheck();
    }
  }

  // Handle label click for radio buttons (improves UX)
  onRadioLabelClick(fieldId: any, value: any): void {
    const fieldIdStr = this.getFieldIdAsString(fieldId);
    const control = this.dynamicForm.get(fieldIdStr);
    if (control) {
      control.setValue(value);
      control.markAsTouched();
      this.cdr.markForCheck();
    }
  }

  getGridClass(columns: number): string {
    switch (columns) {
      case 1:
        return 'grid-cols-1';
      case 2:
        return 'grid-cols-1 md:grid-cols-2';
      case 3:
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
      default:
        return 'grid-cols-1';
    }
  }
}
